/* Pants-card previews: decode before swapping, keep the last selection, preserve native purchase controls. */
(() => {
  const selector = '[data-brl-pants-card]';
  const pendingImages = new Map();
  const cardStates = new WeakMap();

  function stateFor(picker) {
    if (!cardStates.has(picker)) cardStates.set(picker, { revision: 0, clearFade: null });
    return cardStates.get(picker);
  }

  function imageFor(picker) {
    return picker.closest(selector)?.querySelector('.card__media .media > img');
  }

  function prepareImage(picker, variant) {
    const current = imageFor(picker);
    const preview = variant?.featured_media?.preview_image;
    if (!current || !preview?.src) return Promise.resolve(null);
    const source = new URL(preview.src, document.baseURI);
    const key = source.href + '|' + current.sizes + '|' + window.innerWidth;
    if (pendingImages.has(key)) return pendingImages.get(key);
    const sourceWidth = Number(preview.width) || 1066;
    const widths = [165, 360, 533, 720, 940, 1066].filter(width => width < sourceWidth);
    widths.push(sourceWidth);
    const atWidth = width => {
      const url = new URL(source);
      url.searchParams.set('width', width);
      return url.href;
    };
    const promise = new Promise((resolve, reject) => {
      const next = new Image();
      next.decoding = 'async';
      next.sizes = current.sizes;
      next.onload = async () => {
        try {
          if (next.decode) await next.decode();
          if (!next.naturalWidth) throw new Error('Image unavailable');
          resolve(next);
        } catch (error) { reject(error); }
      };
      next.onerror = () => reject(new Error('Image unavailable'));
      next.srcset = widths.map(width => atWidth(width) + ' ' + width + 'w').join(', ');
      next.src = atWidth(Math.min(533, sourceWidth));
    });
    pendingImages.set(key, promise);
    promise.catch(() => { if (pendingImages.get(key) === promise) pendingImages.delete(key); });
    if (pendingImages.size > 40) pendingImages.delete(pendingImages.keys().next().value);
    return promise;
  }

  function variantForSwatch(picker, input) {
    const index = Number(picker.dataset.brlColorIndex || 0);
    const variants = picker.getVariantData();
    return variants.find(variant => variant.available && variant.options[index] === input.value)
      || variants.find(variant => variant.options[index] === input.value);
  }

  function warmSwatch(event) {
    const input = event.target.closest('.color-swatch')?.querySelector('input');
    const picker = input?.closest('card-variant-selects');
    if (!picker?.closest(selector) || !picker.getVariantData) return;
    prepareImage(picker, variantForSwatch(picker, input)).catch(() => {});
  }

  customElements.whenDefined('card-variant-selects').then(() => {
    const prototype = customElements.get('card-variant-selects').prototype;
    if (prototype.brlSmoothMediaInstalled) return;
    const originalUpdateMedia = prototype.updateMedia;
    const originalUpdateMasterId = prototype.updateMasterId;
    prototype.brlSmoothMediaInstalled = true;

    prototype.updateMasterId = function () {
      const card = this.closest(selector);
      if (card) {
        stateFor(this).revision++;
        card.removeAttribute('aria-busy');
      }
      originalUpdateMasterId.call(this);
      if (!this.closest(selector) || this.currentVariant?.available) return;
      const input = this.querySelector('.product-form__input--swatches input:checked');
      const fallback = input && variantForSwatch(this, input);
      if (!fallback?.available) return;
      this.currentVariant = fallback;
      this.options = fallback.options.slice();
      this.querySelectorAll('.product-form__input').forEach((group, index) => {
        group.querySelectorAll('input[type="radio"]').forEach(radio => {
          radio.checked = radio.value === fallback.options[index];
        });
        const select = group.querySelector('select');
        if (select) select.value = fallback.options[index];
      });
    };

    prototype.updateMedia = function () {
      const card = this.closest(selector);
      if (!card) return originalUpdateMedia.call(this);
      const state = stateFor(this);
      const revision = ++state.revision;
      const variant = this.currentVariant;
      const current = imageFor(this);
      if (!variant || !current) return;
      card.classList.add('brl-card-color-selected');
      const destination = new URL(this.dataset.url, document.baseURI);
      destination.searchParams.set('variant', variant.id);
      card.querySelectorAll('.card__heading a, a.go-to-product').forEach(link => {
        link.href = destination.pathname + destination.search;
      });
      card.setAttribute('aria-busy', 'true');
      prepareImage(this, variant).then(next => {
        if (state.revision !== revision || !this.isConnected || !next) return;
        if (state.clearFade) state.clearFade();
        let layer;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion && current.complete && current.naturalWidth && current.currentSrc !== next.currentSrc) {
          layer = document.createElement('span');
          layer.className = 'brl-card-swap-layer';
          layer.setAttribute('aria-hidden', 'true');
          const previous = current.cloneNode(false);
          previous.removeAttribute('id');
          previous.removeAttribute('loading');
          previous.alt = '';
          layer.append(previous);
          current.parentElement.append(layer);
        }
        current.srcset = next.srcset;
        current.src = next.src;
        const color = this.querySelector('.product-form__input--swatches input:checked')?.value;
        const title = card.querySelector('.card__heading')?.textContent.trim() || '';
        current.alt = color ? title + ' — ' + color : title;
        card.removeAttribute('aria-busy');
        if (layer) {
          const animation = layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease-out', fill: 'forwards' });
          const cleanup = () => { animation.cancel(); layer.remove(); if (state.clearFade === cleanup) state.clearFade = null; };
          state.clearFade = cleanup;
          animation.finished.then(cleanup).catch(() => {});
        }
      }).catch(() => {
        // Preserve the previous complete photo if the CDN request fails.
      }).finally(() => {
        if (state.revision === revision) card.removeAttribute('aria-busy');
      });
    };

    document.addEventListener('pointerover', warmSwatch, { passive: true });
    document.addEventListener('pointerdown', warmSwatch, { passive: true });
    document.addEventListener('focusin', warmSwatch);
  });
})();
